import {
  createPostAccessRecipientMutation,
  deletePostAccessRecipientMutation,
  updatePostAccessRecipientMutation,
} from '@/graphql/mutations/Utils.mutation';
import {
  PostAccessRecipientsQuery,
  type PostAccess_Recipient_Fragment$data,
  type PostAccess_Recipients_Query,
} from '@/graphql/queries/PostAccess';
import { PostAccessRecipientStatusEnum } from '@/graphql/types';
import type { FragmentData } from '@/graphql/utils';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicIcon from '@mui/icons-material/Public';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState, useRef, useEffect, useCallback, useMemo, Suspense } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { NodeTypesForStatus } from './utils';

type Recipient = FragmentData<PostAccess_Recipient_Fragment$data> | PostAccess_Recipient_Fragment$data;

type Props<T extends keyof NodeTypesForStatus> = {
  type: T;
  id: string;
};

const emailSchema = z.string().email();

const emailOrDomainSchema = z.string().refine(
  (val) => {
    const domainRegex = /^@([a-zA-Z]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

    if (domainRegex.test(val)) {
      return true;
    }

    return emailSchema.safeParse(val).success;
  },
  {
    message: 'Enter a valid email address (user@example.com) or domain (@example.com)',
  }
);

function validateEmailInput(
  input: string,
  recipients: Recipient[]
): { email: string; domain: string; error: string | null } {
  if (!input) return { email: '', domain: '', error: null };

  const isDomain = input.startsWith('@') && !input.includes('@', 1);
  const normalizedInput = isDomain ? input.slice(1) : input;

  // Prevent duplicates
  if (recipients.some((r) => r.email === input || (r.email === '' && r.domain === normalizedInput.toLowerCase()))) {
    return { email: '', domain: '', error: `${normalizedInput} already exists, try another email or domain` };
  }

  const validation = emailOrDomainSchema.safeParse(input);
  if (!validation.success) {
    return { email: '', domain: '', error: validation.error.errors[0].message };
  }

  if (isDomain) {
    const domain = normalizedInput.toLowerCase();
    const hasEmailWithDomain = recipients.some((r) => r?.email?.toLowerCase().endsWith(`@${domain}`));
    if (hasEmailWithDomain) {
      return { email: '', domain: '', error: 'A recipient with an email from this domain already exists.' };
    }
    return { email: '', domain, error: null };
  }
  // input is an email
  const domain = input?.split('@')?.[1]?.toLowerCase();
  const hasDomainRecipient = recipients?.some((r) => r.email === '' && r.domain === domain);
  if (hasDomainRecipient) {
    return { email: '', domain: '', error: 'A recipient with this domain already exists.' };
  }
  return { email: input, domain: domain || '', error: null };
}

type PostAccessListItemProps = {
  recipient: Recipient;
  parentLoading: boolean;
};

function PostAccessListItem({ recipient, parentLoading }: PostAccessListItemProps) {
  const [loading, setLoading] = useState(false);

  const isDomainOnly = useMemo(() => {
    return recipient.email === '' && recipient.domain !== '';
  }, [recipient.email, recipient.domain]);

  const handleDisable = () => {
    if (!recipient.id) return;
    setLoading(true);
    updatePostAccessRecipientMutation(
      {
        input: {
          id: recipient.id,
          status: recipient.status === PostAccessRecipientStatusEnum.Active ? 'REVOKED' : 'ACTIVE',
        },
      },
      {
        onCompleted(response, errors) {
          setLoading(false);
          if (!!errors?.length) {
            toast.error('Error updating post access recipient status');
            return;
          }
        },
      }
    );
  };

  const handleDelete = () => {
    if (!recipient.id) return;
    setLoading(true);
    deletePostAccessRecipientMutation(
      {
        input: { id: recipient.id },
      },
      {
        onCompleted(response, errors) {
          setLoading(false);
          if (!!errors?.length) {
            toast.error('Error deleting post access recipient');
            return;
          }
        },
      }
    );
  };

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: 0,
        p: 1.5,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        boxShadow: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isDomainOnly ? (
          <LanguageIcon fontSize="small" color="action" />
        ) : (
          <EmailIcon fontSize="small" color="action" />
        )}
        <Typography variant="body2" sx={{ width: 200 }}>
          {isDomainOnly ? `@${recipient.domain}` : recipient.email || `@${recipient.domain}`}
        </Typography>
      </div>
      <div>
        <Typography
          variant="caption"
          sx={{ mr: 1 }}
          color={recipient.status === PostAccessRecipientStatusEnum.Active ? 'success.main' : 'text.secondary'}
        >
          {recipient.status}
        </Typography>
        <Tooltip title={recipient.status === PostAccessRecipientStatusEnum.Active ? 'Disable' : 'Enable'}>
          <span>
            <IconButton
              size="small"
              aria-label={recipient.status === PostAccessRecipientStatusEnum.Active ? 'disable' : 'enable'}
              onClick={handleDisable}
              disabled={loading || parentLoading}
            >
              {recipient.status === PostAccessRecipientStatusEnum.Active ? (
                <BlockIcon fontSize="small" color="warning" />
              ) : (
                <CheckCircleIcon fontSize="small" color="success" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton
              size="small"
              color="error"
              aria-label="delete"
              onClick={handleDelete}
              disabled={loading || parentLoading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </Paper>
  );
}

const MemoizedPostAccessListItem = React.memo(PostAccessListItem);

const PostAccess = <T extends keyof NodeTypesForStatus>(props: Props<T>) => {
  const data = useLazyLoadQuery<PostAccess_Recipients_Query>(PostAccessRecipientsQuery, {
    first: 100,
    where: {
      postId: props.id,
      type: props.type,
    },
  });

  const recipients: Recipient[] = useMemo(() => {
    return data.postAccessRecipients?.edges?.map((edge) => edge.node)?.filter(Boolean) || [];
  }, [data.postAccessRecipients?.edges]);

  // Universal access logic
  const hasUniversal = useMemo(() => {
    return recipients.some((r) => r.domain === '@@universal@@');
  }, [recipients]);

  const handleSetUniversal = useCallback(async () => {
    if (recipients.length >= 1) {
      const confirmed = window.confirm(
        'Enabling universal access will remove all other recipients. Are you sure you want to proceed?'
      );
      if (!confirmed) {
        return;
      }
    }

    setLoading(true);
    Promise.all(
      recipients.map(
        (r) =>
          new Promise((resolve) => {
            if (r.id) {
              deletePostAccessRecipientMutation(
                { input: { id: r.id } },
                {
                  onCompleted(response, errors) {
                    if (!!errors?.length) {
                      resolve(false);
                      return;
                    }
                    resolve(true);
                  },
                }
              );
            } else {
              resolve(true);
            }
          })
      )
    )
      .then(() => {
        createPostAccessRecipientMutation(
          {
            input: {
              email: '',
              domain: '@@universal@@',
              type: props.type,
              status: PostAccessRecipientStatusEnum.Active,
              postId: props.id,
            },
            connections: [data?.postAccessRecipients?.__id || ''],
          },
          {
            onCompleted: () => setLoading(false),
          }
        );
      })
      .catch(() => {
        setLoading(false);
        toast.error('Error setting universal access');
      });
  }, [recipients, props.id]);

  const handleRemoveUniversal = useCallback(() => {
    setLoading(true);
    const universal = recipients.find((r) => r.domain === '@@universal@@');
    if (universal?.id) {
      deletePostAccessRecipientMutation(
        { input: { id: universal.id } },
        {
          onCompleted: () => setLoading(false),
        }
      );
    } else {
      setLoading(false);
    }
  }, [recipients]);

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(''); // Controlled input value
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (modalOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [modalOpen]);

  const handleAdd = useCallback(
    (input: string) => {
      const { email, domain, error } = validateEmailInput(input, recipients);
      if (error) {
        setInputError(error);
        return;
      }

      setInputError(null);
      setLoading(true);
      createPostAccessRecipientMutation(
        {
          input: {
            email,
            domain,
            type: props.type,
            status: PostAccessRecipientStatusEnum.Active,
            postId: props.id,
          },
          connections: [data?.postAccessRecipients?.__id || ''],
        },
        {
          onCompleted(response, errors) {
            setLoading(false);
            if (!!errors?.length) {
              toast.error('Error creating post access recipient');
              return;
            }
            setInputValue(''); // Clear input after successful add
            setTimeout(() => {
              inputRef.current?.focus(); // Re-focus input after clearing
            }, 0);
          },
        }
      );
    },
    [recipients, props.id]
  );

  return (
    <>
      <Button onClick={() => setModalOpen(true)} fullWidth variant="outlined" color="primary">
        Manage Access
        {recipients.length > 0 && ` (${recipients.length})`}
      </Button>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        aria-modal="true"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95vw' : 500,
            minWidth: isMobile ? '90vw' : 400,
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: isMobile ? 2 : 4,
            borderRadius: 2,
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h5" component="h2" sx={{ pr: 4, mb: 0.5 }}>
            Access Control
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ pr: 4, mb: 2 }}>
            Add emails or domains to control who can view this post.
          </Typography>
          {/* Universal access UI logic here */}
          {hasUniversal ? (
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={<PublicIcon />}
                label="Universal Access Enabled (Anyone can view)"
                color="success"
                onDelete={handleRemoveUniversal}
                disabled={loading}
                sx={{ fontWeight: 600, fontSize: 16, p: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                Universal access is enabled. Anyone can request for access to this post.
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                inputRef={inputRef}
                label="Add Email or Domain"
                type="text"
                variant="outlined"
                fullWidth
                autoFocus
                disabled={loading}
                error={!!inputError}
                helperText={inputError || 'Enter an email (user@example.com) or domain (@example.com)'}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  const value = (e?.target as HTMLInputElement)?.value;
                  if (e.key === 'Enter' && value) {
                    handleAdd(value);
                  }
                }}
                placeholder="user@example.com or @example.com"
                sx={{ mb: 1 }}
              />
              <Stack
                spacing={1}
                sx={{
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 1,
                  maxHeight: isMobile ? '40vh' : '45vh',
                  overflowY: 'auto',
                  alignItems: recipients.length === 0 ? 'center' : undefined,
                  justifyContent: recipients.length === 0 ? 'center' : undefined,
                  minHeight: 120,
                }}
              >
                {recipients.length === 0 ? (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                    <LockOutlinedIcon sx={{ fontSize: 48, mb: 1, color: 'grey.400' }} />
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      No access has been configured yet
                    </Typography>
                    <Typography variant="body2">
                      Add an email or domain above to restrict access to this post.
                    </Typography>
                  </Box>
                ) : (
                  recipients.map((recipient, idx) => (
                    <MemoizedPostAccessListItem key={recipient.id} recipient={recipient} parentLoading={loading} />
                  ))
                )}
              </Stack>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                sx={{ mt: 2, fontWeight: 600 }}
                onClick={handleSetUniversal}
                disabled={loading}
                startIcon={<PublicIcon />}
              >
                Enable Universal Access
              </Button>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

const Component = <T extends keyof NodeTypesForStatus>(props: Props<T>) => {
  return (
    <Suspense
      fallback={
        <Button fullWidth variant="outlined" color="primary" disabled>
          Manage Access
        </Button>
      }
    >
      <PostAccess {...props} />
    </Suspense>
  );
};

export { Component as PostAccess };
